/**
 * Точка входу SPA. Порядок старту під LCP на слабкому ПК:
 * 1) public/boot.js (до першого малювання) ставить тему/щільність/легкий режим і вже запросив
 *    метадані першого екрана раннім `fetch()` (`window.__clEarly`): після входу — `/meta` платформи;
 * 2) тут одразу кладемо ті самі запити в кеш TanStack Query: api/client.ts забирає відповіді
 *    ранніх запитів, а не шле нові;
 * 3) рендер; межа першого екрана (perf/first-screen.ts) — перший намальований маршрут: після неї
 *    web-vitals, `/me`, профіль компанії й інше, що першому кадру не потрібне; виміри для
 *    автопропозиції легкого режиму (prefs/lite-watch.ts, ліниво після load) — два
 *    PerformanceObserver, без бібліотек.
 */
import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { App } from './app/App.tsx';
import { createAppRouter } from './app/router.tsx';
import { afterFirstScreen, watchFirstScreen } from './perf/first-screen.ts';
import { startRum } from './perf/rum.ts';
import { onUnauthorized, readRetryDelay, retryRead } from './platform/api.ts';
import { watchBusy } from './platform/busy.ts';
import { hasSessionHint, platformMetaQuery } from './platform/meta.ts';
import { watchNetwork } from './platform/network.ts';
import { isQuiet } from './platform/query-meta.ts';
import { queryPolicy } from './platform/query-policy.ts';
import { watchSignOut } from './platform/sign-out.ts';
import { watchVersion } from './platform/version.ts';
import { liteOfferReason, offerAllowed, readSignals } from './prefs/lite-offer.ts';
import { getPrefs } from './prefs/prefs.ts';
import { loadLiteBanner } from './shell/lite-banner.ts';
import { noteFailure, registerBuild } from './support/diagnostics.ts';
import { clearAllDrafts, initDrafts } from './ui/save/drafts.ts';
// Перші чотири — спільні з документом входу (src/login.tsx), тож у збірці вони йдуть першими.
// Збірка кладе всі стилі першого екрана в один файл `base` (vite.config.ts): каскад у ньому
// такий самий, як порядок імпортів тут, і dev зі збіркою не розходяться.
import './styles/tokens.css';
import './styles/base.css';
import './styles/components.css';
import './styles/brand.css';
import './styles/icons.css';
import './styles/shell.css';
import './styles/screens.css';
import './styles/app.css';

// Ідентифікатор збірки для технічних даних звернення (UX-51): хеш у назві цього файла.
registerBuild(import.meta.url);

// Повтори (UX-18, UX-19): читання — `retryRead` (мережа, 5xx, `Retry-After`), зміни — лише в
// `api()` з тим самим ключем. Мутації не стають на паузу офлайн: прихованої офлайн-черги немає (UX-17).
// Помилки запитів і змін потрапляють у журнал останніх кодів для «Повідомити про проблему»
// (UX-51): лише код і `traceId`, без тексту помилки (support/diagnostics.ts).
const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error, query) => {
      if (!isQuiet(query.meta)) noteFailure(error);
    },
  }),
  mutationCache: new MutationCache({ onError: noteFailure }),
  defaultOptions: {
    queries: {
      ...queryPolicy.default,
      retry: retryRead,
      retryDelay: readRetryDelay,
      refetchOnWindowFocus: false,
    },
    mutations: { networkMode: 'always', retry: false },
  },
});

// Екран входу — окремий документ (login.html, src/login.tsx): сюди він не потрапляє. Після входу
// перший екран будується з `/meta` платформи (навігація, стартова сторінка); без входу застосунок
// веде на вхід (демо S4 без входу прибрано, тікет 8e4d4110).
if (hasSessionHint()) void queryClient.prefetchQuery(platformMetaQuery());
// Чернетки форм (states.md, розд. 7): чужі й прострочені — геть, вихід в іншій вкладці і будь-яка
// відповідь 401 прибирають усі чернетки вкладки.
initDrafts();
onUnauthorized(clearAllDrafts);
// Вихід в іншій вкладці і «Назад» на сторінку з bfcache після виходу — на вхід (platform/sign-out.ts).
watchSignOut(queryClient);
// Зміна чекає `Retry-After` перед повтором — тост з таймером (UX-19).
watchBusy();
// «Немає зв'язку» за фактами: запит не дійшов і перевірка теж (UX-29, platform/network.ts).
watchNetwork();
// «Доступна нова версія» — перевірка документа застосунку раз на годину й після збою чанка (UX-31).
watchVersion();

// Пропозиція легкого режиму (UX-36): чанк банера їде паралельно з рештою старту, і перший рендер
// чекає на нього не довше 250 мс — тоді на першому візиті слабкого ПК банер є в першому кадрі
// (`LiteOfferGate` малює його лише разом з екраном, lite-mode.md, розд. 2). Пізніше чанк
// приїде вже на наступному екрані.
const offerChunk =
  liteOfferReason(getPrefs(), navigator, readSignals(), Date.now()) === null
    ? null
    : Promise.race([loadLiteBanner(), new Promise<void>((r) => window.setTimeout(r, 250))]);

const router = createAppRouter(queryClient);
// Межа першого екрана: перший маршрут, намальований з даними (`onRendered`), кадр і простій.
watchFirstScreen(router);
const root = document.getElementById('root');
const mount = () => {
  if (root) {
    createRoot(root).render(
      <StrictMode>
        <App queryClient={queryClient} router={router} />
      </StrictMode>,
    );
  }
};
if (offerChunk) void offerChunk.then(mount);
else mount();
// Таблиця браузерів — лінивий чанк: у перший екран не входить. Бібліотека — після межі першого
// екрана: буферизовані записи (LCP, event timing) вона прочитає й тоді.
startRum(
  () => import('./support/browser.ts').then((m) => m.browserBucket(navigator.userAgent)),
  (run) => void afterFirstScreen(run),
);
// Виміри для автопропозиції легкого режиму — окремий чанк після `load`, лише коли пропонувати можна.
if (offerAllowed(getPrefs(), Date.now())) {
  const watch = () =>
    void import('./prefs/lite-watch.ts').then((m) => m.startLiteWatch(getPrefs()));
  if (document.readyState === 'complete') watch();
  else window.addEventListener('load', watch, { once: true });
}
